import { useQuery } from '@tanstack/react-query';
import { OFT_ABI } from '../config/oft';
import { DEPOSIT_CHAIN_BY_ID, INK_DEPOSIT_CHAIN } from '../config/chains';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';

export interface OftQuoteSendData {
  /** Native fee (ETH) the user pays on the source chain as msg.value */
  nativeFee: bigint;
  /** LZ token fee (usually 0) */
  lzTokenFee: bigint;
}

/**
 * Calls `quoteSend()` on the source chain's OFT contract to get the messaging fee.
 *
 * The nativeFee is added to msg.value in the actual `send()` call. It is NOT
 * deducted from the USDT0 amount.
 *
 * @param sourceChainId - EVM chain ID of the source chain
 * @param amount - Amount to send in token's local decimals (6 for USDT0)
 * @param extraOptions - The extra options bytes (from buildExtraOptions)
 */
export function useOftQuoteSend(
  sourceChainId: number | undefined,
  amount: bigint | undefined,
  extraOptions: `0x${string}`,
) {
  return useQuery<OftQuoteSendData>({
    queryKey: ['oftQuoteSend', sourceChainId, amount?.toString(), extraOptions],
    queryFn: async () => {
      if (!sourceChainId || amount === undefined) {
        throw new Error('sourceChainId and amount are required');
      }

      const chainConfig = DEPOSIT_CHAIN_BY_ID[sourceChainId];
      if (!chainConfig) {
        throw new Error(`No chain config for chainId ${sourceChainId}`);
      }

      const publicClient = getSourceChainPublicClient(sourceChainId);

      const sendParam = {
        dstEid: INK_DEPOSIT_CHAIN.lzEndpointId,
        to: ('0x' + '0'.repeat(64)) as `0x${string}`, // zero address bytes32 for quote
        amountLD: amount,
        minAmountLD: BigInt("0"), // zero for quote
        extraOptions,
        composeMsg: '0x' as `0x${string}`,
        oftCmd: '0x' as `0x${string}`,
      };

      const msgFee = await publicClient.readContract({
        address: chainConfig.oftAddress,
        abi: OFT_ABI,
        functionName: 'quoteSend',
        args: [sendParam, false],
      });

      return {
        nativeFee: msgFee.nativeFee,
        lzTokenFee: msgFee.lzTokenFee,
      };
    },
    enabled: !!sourceChainId && amount !== undefined && amount > BigInt("0"),
    staleTime: 30_000,
  });
}
